import React, { useCallback, useEffect, useState } from 'react';
import { Button, ModalComponent } from '../baseComponents';
import { ResumeIcon } from 'src/icons';
import { Select, Modal } from 'antd';
import { useWakeLock } from 'react-screen-wake-lock';
import { doStream, endStream } from '../../utils/listen';
import {
  AuraDemoEncounter,
  completeEncounter,
  specialtiesConversation,
  startEncounter,
} from '../../store/actions/encounter';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { MicrophoneLevel } from '../baseComponents/MicrophoneLevel';
import AmbientAnimation from '../CurrentEncounter/AmbientAnimation';
import AmbientAnimationPaused from '../CurrentEncounter/AmbientAnimationPaused';
import { CheckOutlined } from '@ant-design/icons';
import { MobileLiveTranscription } from '../PastEncounter/MobileLiveTranscription';
import { useNavigate } from 'react-router-dom';
import { Bars } from 'react-loader-spinner';
import { Scrollbar } from 'react-scrollbars-custom';
import { useAuth, useFeatureEntitlements } from '@frontegg/react';
import { analytics } from 'src/services';

const encounterPhase = 'in-visit';

const GenerateSummaryButton = ({ handleCompleteRecording, disabled }) => {
  return (
    <Button
      type={'button'}
      className="bg-red-500 hover:bg-red-400 text-white font-normal p-2 py-2 px-5 rounded-xl focus:outline-none focus:shadow-outline w-full"
      onClick={handleCompleteRecording}
      disabled={disabled}
    >
      <span className="mr-2 mt-0">
        <CheckOutlined />
      </span>
      Stop transcribing
    </Button>
  );
};

export const AuraSignUpModal = ({
  open: openProp,
  createDemoEncounter,
  onClose,
  setActiveTab,
  storedParams,
  selectedRecord,
  encounterStatus,
  setEncounterStatus,
  setLiveTranscript,
  setLivePartialTranscript,
  isStreaming,
  setIsStreaming,
  isRecording,
  setIsRecording,
  fullTranscript,
  setFullTranscript,
  livePartialTranscript,
  liveTranscript,
  setActiveRightPanelTab,
}) => {
  const [micLevel, setMicLevel] = useState(0);
  const [isStarting, setIsStarting] = useState(false);
  const {
    auraDemoConversation,
    isEncounterCreateLoading,
    isEncounterCompleteLoading,
  } = useSelector((state) => state?.encounterState);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isSupported, released, request, release } = useWakeLock();
  const [microphones, setMicrophones] = useState([]);
  const [selectedMicrophone, setSelectedMicrophone] = useState(null);
  const [accessRequested, setAccessRequested] = useState(false);
  const [showMicrophoneBox, setShowMicrophoneBox] = useState(false);
  const [openingRecording, setOpeningRecording] = useState(false);
  const { user } = useAuth();

  const { settingsOrgValue } = useSelector((state) => state?.appointmentState);
  const encounter = selectedRecord;

  const { isEntitled: hasICD10Access } = useFeatureEntitlements('icd-10');

  const isRetryDemo = openProp?.retry;

  const handleCleanupRecording = useCallback(() => {
    setIsRecording(false);
    setIsStreaming(false);
    endStream();
  }, [setIsRecording, setIsStreaming]);

  useEffect(() => {
    if (!isSupported) {
      console.warn('Screen Wake Lock is not supported');
      return;
    }
    if (isRecording) {
      request().catch((err) => {
        console.error(err);
      });
    } else {
      if (!released) {
        release();
      }
    }
  }, [isRecording, request, release, isSupported, released]);

  useEffect(() => {
    if (!settingsOrgValue?.onboarding_demo) {
      // eslint-disable-next-line no-undef
      analytics.track('Viewed Onboarding Demo', { email: user?.email });
    }
  }, [settingsOrgValue, user?.email]);

  useEffect(() => {
    dispatch(specialtiesConversation());
  }, [dispatch]);

  useEffect(() => {
    // To ensure when switching tabs, we pause the recording.
    handleCleanupRecording();
  }, []);

  useEffect(() => {
    return () => {
      // This function will be called when the component is unmounted
      // This is to ensure if the user starts a mic and navigates away, it's paused
      // this is also a fix for the "delay" in transcription.
      handleCleanupRecording();
    };
  }, []);

  const handleSocketErrors = (error) => {
    // eslint-disable-next-line no-undef
    analytics.track(
      `User Run Into Socket Error: ${error}, Pausing Encounter In Aura Demo`,
      { email: user?.email }
    );
    let modalOptions = {
      centered: true,
      className: 'font-sans',
    };
    if (error === 'MICROPHONE_SETUP_FAILED') {
      Modal.error({
        ...modalOptions,
        title: 'Microphone setup failed',
        content:
          'Unable to access a microphone. Please check your microphone settings and permissions.',
      });
    } else if (error === 'AUDIO_CONTEXT_SUSPENDED') {
      Modal.warning({
        ...modalOptions,
        title: 'Microphone was suspended',
        content:
          'Microphone was suspended. Please click resume to continue transcribing.',
      });
    } else if (error === 'NETWORK_ERROR') {
      Modal.error({
        ...modalOptions,
        title: 'Network error',
        content:
          'You were offline for more than 1 minute. Please click resume to continue transcribing.',
      });
    } else if (error === 'NETWORK_INIT_ERROR') {
      Modal.error({
        ...modalOptions,
        title: 'Network offline',
        content:
          'Could not connect to server. Please check your connection and try again.',
      });
    } else if (error === 'MAX_RECONNECTIONS_EXCEEDED') {
      Modal.error({
        ...modalOptions,
        title: 'Internal server error',
        content:
          'Maximum reconnection attempts reached. Please try again or contact support.',
      });
    }
    handleCleanupRecording();

    const audio = new Audio('src/assets/error.mp3');
    audio.play();
  };

  const [isTranscriptTimeout, setIsTranscriptTimeout] = useState(false);

  const handleTranscriptTimeout = (error) => {
    if (error === 'NO_TRANSCRIPTION_RECEIVED') {
      setIsTranscriptTimeout(true);
    } else if (error !== 'NO_TRANSCRIPTION_RECEIVED') {
      setIsTranscriptTimeout(false);
    }
  };

  //Function when complete recording is clicked
  const handleCompleteRecording = () => {
    if (isRetryDemo) {
      // eslint-disable-next-line no-undef
      analytics.track('Clicked Stop Recording In Aura Retry Demo', {
        email: user?.email,
      });
    } else {
      // eslint-disable-next-line no-undef
      analytics.track('Clicked Stop Recording In Aura Onboarding Demo', {
        email: user?.email,
      });
    }
    setEncounterStatus('summary_inprogress');

    dispatch(
      completeEncounter(encounter?.encounter_id, encounterPhase, storedParams)
    ).then(() => {
      dispatch(AuraDemoEncounter());
      onClose();
      setIsStreaming(false);
      setIsRecording(false);
      setMicLevel(0);
      endStream();
      setActiveTab ? setActiveTab('note') : null;
      isMobileView &&
        navigate(`/mobileEncounterDetails/${encounter?.encounter_id}`);
      if (hasICD10Access && encounterPhase == 'in-visit') {
        setActiveRightPanelTab('icd10');
      }
    });
  };

  const handleStartEncounter = (encounter_phase) => {
    // eslint-disable-next-line no-undef
    analytics.track(
      `Clicked Start Recording In Aura ${isRetryDemo ? 'Retry' : 'Onboarding'} Demo`,
      {
        email: user?.email,
      }
    );
    setIsRecording(true);
    setIsStarting(true);
    dispatch(
      startEncounter(
        encounter?.encounter_id,
        encounter_phase,
        Object.fromEntries(
          Object.entries({
            summary_verbosity: encounter?.summary_verbosity,
            template_id: encounter?.in_visit_template_id,
            template_overrides_id: encounter?.in_visit_template_overrides_id,
          }).filter(([_, value]) => value)
        ),
        storedParams
      )
    );

    doStream({
      encounterId: encounter?.encounter_id,
      encounterPhase,
      setMicLevel,
      setLiveTranscript,
      setLivePartialTranscript,
      selectedMicrophoneId: selectedMicrophone,
      successCallback: () => {
        setIsStreaming(true);
        setIsStarting(false);
      },
      failureCallback: (error) => {
        handleSocketErrors(error);
      },
      setDictation: () => {},
      isDictate: false,
      transcriptTimeoutCallback: (error) => {
        handleTranscriptTimeout(error);
      },
    });
  };

  useEffect(() => {
    const getMicrophones = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputDevices = devices.filter(
          (device) => device.kind === 'audioinput'
        );
        setMicrophones(audioInputDevices);

        if (audioInputDevices.length > 0) {
          const selectedMicStillAvailable = audioInputDevices.some(
            (device) => device.deviceId === selectedMicrophone
          );
          if (!selectedMicStillAvailable) {
            setSelectedMicrophone(audioInputDevices[0].deviceId);
          }
        }
      } catch (error) {
        console.error('Error enumerating devices:', error);
      }
    };
    getMicrophones();
    navigator.mediaDevices.addEventListener('devicechange', getMicrophones);

    return () => {
      navigator.mediaDevices.removeEventListener(
        'devicechange',
        getMicrophones
      );
    };
  }, [selectedMicrophone, accessRequested]);

  const RequestAccess = async () => {
    let retval = false;
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      retval = true;
    } catch (err) {
      console.error('Error accessing microphone:', err);
      retval = false;
    }
    dispatch({
      type: 'MICROPHONE_ACCESS',
      payload: retval,
    });
  };

  const handleMicrophoneChange = async (deviceId) => {
    if (isRetryDemo) {
      // eslint-disable-next-line no-undef
      analytics.track('Selected Microphone Options In Aura Retry Demo', {
        email: user?.email,
      });
    } else {
      // eslint-disable-next-line no-undef
      analytics.track('Selected Microphone Options In Aura Onboarding Demo', {
        email: user?.email,
      });
    }
    setSelectedMicrophone(deviceId);
  };

  const handleAllowMicrophone = async () => {
    if (isRetryDemo) {
      // eslint-disable-next-line no-undef
      analytics.track('Clicked Allow Microphone In Aura Retry Demo', {
        email: user?.email,
      });
    } else {
      // eslint-disable-next-line no-undef
      analytics.track('Clicked Allow Microphone In Aura Onboarding Demo', {
        email: user?.email,
      });
    }
    if (!accessRequested) {
      await RequestAccess();
      setAccessRequested(true);
    }

    setShowMicrophoneBox(true);
  };

  const handleStartTranscribing = async () => {
    // eslint-disable-next-line no-undef
    analytics.track(
      `Clicked  Start Recording A Demo Session In Aura ${isRetryDemo ? 'Retry' : 'Onboarding'} Demo`,
      {
        email: user?.email,
      }
    );

    createDemoEncounter({
      callback: () => {
        setOpeningRecording(true);
      },
    });
  };

  const isMobileView = window.innerWidth <= 1260;

  const AuraConversation = [
    { You: 'Hi, what brings you to the emergency room today?' },
    {
      Patient:
        "I've been having severe back pain for the past few hours. It's so intense that I can barely move.",
    },
    {
      You: "We need to act quickly. We'll take some blood tests and do an MRI to check what's causing the pain. Have you had any previous back issues?",
    },
    { You: 'No, this is the first time.' },
    {
      You: "Alright. The MRI shows a herniated disc pressing on a nerve, which is causing the severe pain. We're going to give you some pain relief medication and a muscle relaxant to stabilize you. You'll be admitted to the orthopedic unit for close monitoring and to discuss further treatment options, which may include physical therapy or, if necessary, surgery.",
    },
    { Patient: 'Thanks doc.' },
  ];

  return (
    <>
      <ModalComponent
        centered={true}
        open={!!openProp}
        width={isMobileView ? null : openingRecording ? '70%' : '45%'}
        submitButtonText=""
        cancelButtonText=""
        closable={!!isRetryDemo}
        handleCancel={() => {
          if (isRetryDemo) {
            onClose();
          }
        }}
        additionalBody={
          <>
            <div className="flex flex-col items-center">
              {!openingRecording ? (
                <>
                  <div className="px-10">
                    <div className="flex mb-6 mt-10 font-semibold text-sm lg:text-xl">
                      Try a demo session Aura AI Scribe Yourself 🚀
                    </div>
                    <div className="text-base font-normal flex mb-3">
                      Welcome! 🌟
                    </div>
                    <div className="text-base font-normal flex mb-3">
                      Experience Aura AI Scribe hands-on and discover its
                      powerful features.
                    </div>
                    <div className="text-base font-normal flex mb-6">
                      {`We can't wait to see what you'll create! 😊`}
                    </div>
                  </div>
                  {showMicrophoneBox ? (
                    <>
                      <Select
                        placement="topRight"
                        optionFilterProp="children"
                        value={selectedMicrophone}
                        onChange={(deviceId) =>
                          handleMicrophoneChange(deviceId)
                        }
                        bordered={false}
                        className={`w-4/5 flex shadow-sm border bg-white border-gray-200 rounded-full h-10 `}
                      >
                        {microphones.map((microphone) => (
                          <Select.Option
                            key={microphone.deviceId}
                            value={microphone.deviceId}
                          >
                            {microphone.label ||
                              `Microphone ${microphone.deviceId}`}
                          </Select.Option>
                        ))}
                      </Select>
                      <button
                        className="bg-[#00D090] hover:bg-[#059669] flex items-center mt-5 justify-center text-base text-white rounded-full py-2 px-6"
                        onClick={handleStartTranscribing}
                        disabled={isEncounterCreateLoading}
                      >
                        Start transcribing a demo session
                      </button>
                    </>
                  ) : (
                    <div className="inline-block">
                      <button
                        className="bg-[#00D090] hover:bg-[#059669] flex items-center mt-5 justify-center text-base text-white rounded-full py-2 px-8"
                        type="button"
                        onClick={handleAllowMicrophone}
                      >
                        Click here to allow microphone
                      </button>
                    </div>
                  )}
                </>
              ) : openingRecording ? (
                <>
                  {isMobileView ? (
                    <div className="grid sm:grid-cols-1 lg:grid-cols-2">
                      <div className="rounded-xl flex flex-col items-center justify-center relative current">
                        <div
                          className={`${window.innerWidth <= 1260 ? 'w-full' : ''} items-center justify-center `}
                        >
                          <div className="">
                            {isStreaming ? (
                              <div className="">
                                <AmbientAnimation />
                                <div className="w-full mb-4">
                                  <MicrophoneLevel
                                    micLevel={micLevel}
                                    isTranscriptTimeout={isTranscriptTimeout}
                                  />
                                </div>
                              </div>
                            ) : (
                              <div className="">
                                <AmbientAnimationPaused />
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="justify-center items-center w-[300px]">
                          <div className="w-full">
                            {isStreaming && !isStarting ? (
                              // will trigger once the recording is started/resumed
                              <div className="flex space-x-4 mt-4 justify-center">
                                <GenerateSummaryButton
                                  handleCompleteRecording={
                                    handleCompleteRecording
                                  }
                                  disabled={isEncounterCompleteLoading}
                                />
                              </div>
                            ) : (
                              <div className="flex mt-5 ">
                                <div className="w-full rounded-xl flex justify-center items-center">
                                  <Button
                                    type={'button'}
                                    className={'w-full'}
                                    disabled={isEncounterCreateLoading}
                                    onClick={() =>
                                      handleStartEncounter(encounterPhase)
                                    }
                                  >
                                    {isRecording ? (
                                      <>
                                        <span className="mr-2 mt-0">
                                          <ResumeIcon />
                                        </span>
                                        Starting ...
                                      </>
                                    ) : (
                                      <>
                                        <span className="mr-2 mt-0">
                                          <ResumeIcon />
                                        </span>
                                        Start transcribing
                                      </>
                                    )}
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="">
                        <div className="text-black mt-10 font-semibold text-xl lg:text-2xl mb-2 text-center">
                          Read this entire script aloud now
                        </div>
                        <p className="text-gray-400 text-sm mb-4 font-medium text-center">
                          Make sure you read both the clinician and patient
                          parts!
                        </p>

                        <div className="shadow-md rounded-md w-full h-56 bg-white ">
                          <Scrollbar>
                            <div className="p-5">
                              {(auraDemoConversation &&
                              auraDemoConversation?.length > 0
                                ? auraDemoConversation
                                : AuraConversation
                              ).map((line, index) => (
                                <div
                                  id="specialty_conversation"
                                  key={index}
                                  className="mb-3 flex gap-2 text-justify"
                                >
                                  {Object.keys(line).map((speaker) => (
                                    <React.Fragment key={speaker}>
                                      <div className="font-bold text-black">
                                        {speaker}:
                                      </div>
                                      <div className="font-medium">
                                        {line[speaker]}
                                      </div>
                                    </React.Fragment>
                                  ))}
                                </div>
                              ))}
                            </div>
                          </Scrollbar>
                        </div>
                        <div className="w-full overflow-y-auto h-32 mt-5 flex justify-center">
                          {encounterPhase != 'after-visit' && (
                            <MobileLiveTranscription
                              fullTranscript={fullTranscript}
                              setFullTranscript={setFullTranscript}
                              liveTranscript={liveTranscript}
                              livePartialTranscript={livePartialTranscript}
                              setLivePartialTranscript={
                                setLivePartialTranscript
                              }
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="grid sm:grid-cols-1 lg:grid-cols-2">
                      <div className="">
                        <div className="text-black font-semibold text-2xl mb-2 text-center">
                          Read this entire script aloud now
                        </div>
                        <p className="text-gray-400 text-sm mb-4 font-medium text-center">
                          Make sure you read both the clinician and patient
                          parts!
                        </p>

                        <div className="bg-white shadow-md rounded-md w-full p-5 h-96 overflow-auto">
                          {(auraDemoConversation &&
                          auraDemoConversation?.length > 0
                            ? auraDemoConversation
                            : AuraConversation
                          ).map((line, index) => (
                            <div
                              id="specialty_conversation"
                              key={index}
                              className="mb-3 flex gap-2 text-justify"
                            >
                              {Object.keys(line).map((speaker) => (
                                <React.Fragment key={speaker}>
                                  <div className="font-bold text-black">
                                    {speaker}:
                                  </div>
                                  <div className="font-medium">
                                    {line[speaker]}
                                  </div>
                                </React.Fragment>
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="rounded-xl flex flex-col items-center justify-center relative current">
                        <div
                          className={`${window.innerWidth <= 1260 ? 'w-full' : ''} items-center justify-center `}
                        >
                          <div className="">
                            {isStreaming ? (
                              <div className="">
                                <AmbientAnimation />
                                <div className="w-full mb-4">
                                  <MicrophoneLevel micLevel={micLevel} />
                                </div>
                              </div>
                            ) : (
                              <div className="">
                                <AmbientAnimationPaused />
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="justify-center items-center w-[300px]">
                          <div className="w-full">
                            {isStreaming && !isStarting ? (
                              // will trigger once the recording is started/resumed
                              <div className="flex space-x-4 mt-4 justify-center">
                                <GenerateSummaryButton
                                  handleCompleteRecording={
                                    handleCompleteRecording
                                  }
                                  disabled={isEncounterCompleteLoading}
                                />
                              </div>
                            ) : (
                              <div className="flex mt-5 ">
                                <div className="w-full rounded-xl flex justify-center items-center">
                                  <Button
                                    type={'button'}
                                    className={'w-full'}
                                    onClick={() =>
                                      handleStartEncounter(encounterPhase)
                                    }
                                  >
                                    {isRecording ? (
                                      <>
                                        <span className="mr-2 mt-0">
                                          <ResumeIcon />
                                        </span>
                                        Starting ...
                                      </>
                                    ) : (
                                      <>
                                        <span className="mr-2 mt-0">
                                          <ResumeIcon />
                                        </span>
                                        Start transcribing
                                      </>
                                    )}
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="w-full overflow-y-auto h-32 mt-5 flex justify-center">
                          {encounterPhase != 'after-visit' && (
                            <MobileLiveTranscription
                              fullTranscript={fullTranscript}
                              setFullTranscript={setFullTranscript}
                              liveTranscript={liveTranscript}
                              livePartialTranscript={livePartialTranscript}
                              setLivePartialTranscript={
                                setLivePartialTranscript
                              }
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex justify-center items-center">
                  <Bars
                    height="25"
                    width="25"
                    color="#00D090"
                    ariaLabel="bars-loading"
                  />
                </div>
              )}
            </div>
          </>
        }
      />
    </>
  );
};
